import axios, { AxiosResponse } from 'axios';

/**
 * API Configuration Constants
 */
const getApiBaseUrl = () => {
	const apiUrl = process.env.VITE_API_URL || import.meta.env.VITE_API_URL;

	if (import.meta.env.DEV) {
		return '/api/v1';
	}

	return `${apiUrl}/api/v1` || 'http://localhost:3000/api/v1';
};

const API_BASE_URL = getApiBaseUrl();

/**
 * Get authentication token from storage
 */
const getAuthToken = (): string | null => {
	return sessionStorage.getItem('auth_token');
};

/**
 * Webhook API Key interfaces
 */
export interface WebhookApiKey {
	id: string;
	type: 'api-key' | 'basic-auth';
	keyId: string;
	username?: string;
	name: string;
	description?: string;
	scopes: string[];
	rateLimitRpm: number;
	rateLimitRph: number;
	isActive: boolean;
	createdBy: string;
	lastUsedAt?: string;
	usageCount: number;
	expiresAt?: string;
	createdAt: string;
	updatedAt: string;
}

export interface CreateWebhookApiKeyRequest {
	type?: 'api-key' | 'basic-auth';
	name: string;
	username?: string;
	password?: string;
	description?: string;
	scopes?: string[];
	rateLimitRpm?: number;
	rateLimitRph?: number;
	expiresAt?: Date;
}

export interface CreateWebhookApiKeyResponse {
	id: string;
	type: 'api-key' | 'basic-auth';
	keyId: string;
	username?: string;
	secret: string;
	name: string;
	description?: string;
	scopes: string[];
	rateLimitRpm: number;
	rateLimitRph: number;
	isActive: boolean;
	createdBy: string;
	lastUsedAt?: string;
	usageCount: number;
	expiresAt?: string;
	createdAt: string;
	updatedAt: string;
}

export interface UpdateWebhookApiKeyRequest {
	name?: string;
	description?: string;
	rateLimitRpm?: number;
	rateLimitRph?: number;
	isActive?: boolean;
	expiresAt?: Date;
}

/**
 * API client for webhook API key management
 */
class WebhookApiKeysClient {
	private readonly timeout: number = 30000;

	/**
	 * Get authentication headers for API requests
	 */
	private getAuthHeaders(): Record<string, string> {
		const token = getAuthToken();
		return {
			'Content-Type': 'application/json',
			...(token && { Authorization: `Bearer ${token}` }),
		};
	}

	/**
	 * Handle API errors consistently
	 */
	private handleError(error: any): never {
		if (axios.isAxiosError(error)) {
			const errorMessage = error.response?.data?.message || error.message;
			throw new Error(errorMessage);
		}
		throw new Error(error.message || 'Unknown error occurred');
	}

	/**
	 * Create a new webhook API key
	 */
	async createApiKey(data: CreateWebhookApiKeyRequest): Promise<CreateWebhookApiKeyResponse> {
		try {
			const response: AxiosResponse<CreateWebhookApiKeyResponse> = await axios.post(
				`${API_BASE_URL}/composer/webhook-api-keys`,
				data,
				{
					headers: this.getAuthHeaders(),
					timeout: this.timeout,
				}
			);
			return response.data;
		} catch (error) {
			this.handleError(error);
		}
	}

	/**
	 * Get all webhook API keys for the current user
	 */
	async getApiKeys(): Promise<WebhookApiKey[]> {
		try {
			const response: AxiosResponse<WebhookApiKey[]> = await axios.get(
				`${API_BASE_URL}/composer/webhook-api-keys`,
				{
					headers: this.getAuthHeaders(),
					timeout: this.timeout,
				}
			);
			return response.data;
		} catch (error) {
			this.handleError(error);
		}
	}

	/**
	 * Get a specific webhook API key by ID
	 */
	async getApiKey(id: string): Promise<WebhookApiKey> {
		try {
			const response: AxiosResponse<WebhookApiKey> = await axios.get(
				`${API_BASE_URL}/composer/webhook-api-keys/${id}`,
				{
					headers: this.getAuthHeaders(),
					timeout: this.timeout,
				}
			);
			return response.data;
		} catch (error) {
			this.handleError(error);
		}
	}

	/**
	 * Update a webhook API key
	 */
	async updateApiKey(id: string, data: UpdateWebhookApiKeyRequest): Promise<WebhookApiKey> {
		try {
			const response: AxiosResponse<WebhookApiKey> = await axios.patch(
				`${API_BASE_URL}/composer/webhook-api-keys/${id}`,
				data,
				{
					headers: this.getAuthHeaders(),
					timeout: this.timeout,
				}
			);
			return response.data;
		} catch (error) {
			this.handleError(error);
		}
	}

	/**
	 * Delete a webhook API key
	 */
	async deleteApiKey(id: string): Promise<void> {
		try {
			await axios.delete(`${API_BASE_URL}/composer/webhook-api-keys/${id}`, {
				headers: this.getAuthHeaders(),
				timeout: this.timeout,
			});
		} catch (error) {
			this.handleError(error);
		}
	}
}

// Export singleton instance
export const webhookApiKeysClient = new WebhookApiKeysClient();
